import { useState, type FC } from "react"

import type {
  ConsumptionCandidateDto,
  ConsumptionSourceSystem,
} from "~/api/dto/proposal-consumption.dto"
import useApplySolarProposalGreenButton from "~/api/hooks/useApplySolarProposalGreenButton"
import useProposalConsumptionSelection from "~/api/hooks/useProposalConsumptionSelection"
import useSwitchProposalConsumptionSelection from "~/api/hooks/useSwitchProposalConsumptionSelection"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  BLOCKED_REASON_COPY,
  formatCoveragePercent,
  LOCKED_CONSUMPTION_RESUME_COPY,
  LOCKED_HOUSEHOLD_CONSUMPTION_COPY,
  QUALITY_LABELS,
  SOURCE_LABELS,
} from "./consumption-source-labels"
import {
  SELECTION_ONLY_GRID_IMPORT_COPY,
  SELECTION_ONLY_MANUAL_SOURCE_COPY,
  SELECTION_ONLY_NEWER_DATA_COPY,
  type GreenbuttonApplyMode,
} from "./greenbutton-apply-mode"

const sourceDataLabel = (sourceSystem: ConsumptionSourceSystem): string =>
  `${SOURCE_LABELS[sourceSystem]} data`

const formatCandidateKwh = (value: number | null): string =>
  value === null
    ? `Annual usage unavailable`
    : `${Math.round(value).toLocaleString()} kWh / year`

/**
 * Side-by-side view of the Proposal's selectable consumption sources — the
 * current Vision profile and the Site's eligible Green Button profile — with
 * an explicit, reversible switch between them.
 *
 * In `selection_only` mode (Solar V2) only the Green Button direction is a
 * switch, through V2's endpoint; manual usage is entered in its own step.
 */
export const ConsumptionSourceComparison: FC<{
  /** Set when the proposal's selections are locked: switching is disabled. */
  applyLockedReason?: string | null
  applyMode?: GreenbuttonApplyMode
  hideBlockedHouseholdApply?: boolean
  onSelectionApplied?: () => Promise<unknown> | void
  proposalId: string
  siteId: string
}> = ({
  applyLockedReason = null,
  applyMode = `v1`,
  hideBlockedHouseholdApply = false,
  onSelectionApplied,
  proposalId,
  siteId,
}) => {
  const consumption = useProposalConsumptionSelection(proposalId)
  const v1Switch = useSwitchProposalConsumptionSelection()
  const selectionOnlySwitch = useApplySolarProposalGreenButton()
  const selectionOnly = applyMode === `selection_only`
  const switchSelection = selectionOnly ? selectionOnlySwitch : v1Switch
  const [pendingConfirmationProfileId, setPendingConfirmationProfileId] =
    useState<string | null>(null)

  const candidates = consumption.data?.candidates ?? []
  const hasGreenButtonCandidate = candidates.some(
    (candidate) => candidate.sourceSystem === `ARCADIA_PLUG`,
  )

  if (!consumption.data || !hasGreenButtonCandidate) {
    return null
  }

  const selectionLocked = Boolean(consumption.data.selection?.lockedAt)
  const selectedSourceSystem = consumption.data.selectedProfile?.sourceSystem
  const switchBlocked = switchSelection.isLoading || Boolean(applyLockedReason)
  const newerGreenButtonData =
    selectionOnly &&
    selectedSourceSystem === `ARCADIA_PLUG` &&
    candidates.some(
      (candidate) =>
        candidate.sourceSystem === `ARCADIA_PLUG` &&
        candidate.selectable &&
        !candidate.selected,
    )

  const handleSwitch = (
    candidate: ConsumptionCandidateDto,
    confirmedDegradedUsage = false,
  ) => {
    if (switchBlocked) {
      return
    }

    if (candidate.requiresConfirmation && !confirmedDegradedUsage) {
      setPendingConfirmationProfileId(candidate.profileId)
      return
    }

    const onSuccess = () => {
      setPendingConfirmationProfileId(null)
      void onSelectionApplied?.()
    }

    if (selectionOnly) {
      selectionOnlySwitch.mutate(
        {
          proposalId,
          siteId,
          action: `switch`,
          body: { profileId: candidate.profileId, confirmedDegradedUsage },
        },
        { onSuccess },
      )
      return
    }

    v1Switch.mutate(
      {
        proposalId,
        siteId,
        profileId: candidate.profileId,
        confirmedDegradedUsage,
        requestId: crypto.randomUUID(),
      },
      { onSuccess },
    )
  }

  return (
    <section className="mt-7" aria-labelledby="consumption-source-comparison">
      <h3
        id="consumption-source-comparison"
        className="font-display text-lg font-bold text-foreground"
      >
        Consumption source
      </h3>
      <p className="mt-1 text-sm font-semibold text-muted">
        {selectedSourceSystem
          ? `This proposal currently uses ${sourceDataLabel(
              selectedSourceSystem,
            )}.`
          : `No consumption source is selected yet.`}
      </p>

      {newerGreenButtonData ? (
        <p
          className="mt-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm font-semibold text-foreground"
          data-testid="greenbutton-newer-data"
        >
          {SELECTION_ONLY_NEWER_DATA_COPY}
        </p>
      ) : null}

      {applyLockedReason ? (
        <p
          className="mt-3 rounded-xl border border-warning/40 bg-warning/15 px-4 py-3 text-sm font-semibold text-foreground"
          data-testid="greenbutton-apply-locked"
        >
          {applyLockedReason}
        </p>
      ) : null}

      {selectionLocked ? (
        <p className="mt-3 rounded-xl border border-warning/40 bg-warning/15 px-4 py-3 text-sm font-semibold text-foreground">
          {hideBlockedHouseholdApply
            ? LOCKED_HOUSEHOLD_CONSUMPTION_COPY
            : LOCKED_CONSUMPTION_RESUME_COPY}
        </p>
      ) : null}

      <ul className="mt-3 grid grid-cols-1 gap-3">
        {candidates.map((candidate) => (
          <li
            key={candidate.profileId}
            className={[
              `rounded-xl border p-4`,
              candidate.selected
                ? `border-success/20 bg-success/5`
                : `border-border bg-surface`,
            ].join(` `)}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display text-base font-bold text-foreground">
                  {sourceDataLabel(candidate.sourceSystem)}
                </p>
                <p className="mt-0.5 font-semibold tabular-nums text-muted">
                  {formatCandidateKwh(candidate.annualEnergyKwh)}
                </p>
              </div>
              {candidate.selected ? (
                <span className="inline-flex rounded-full border border-success/20 bg-success/5 px-3 py-1 text-sm font-semibold text-success">
                  Selected
                </span>
              ) : (hideBlockedHouseholdApply &&
                  candidate.selectionBlockedReason) ||
                (selectionOnly &&
                  candidate.sourceSystem !== `ARCADIA_PLUG`) ? null : (
                <button
                  type="button"
                  disabled={!candidate.selectable || switchBlocked}
                  className={[
                    `rounded-lg px-4 py-2 text-sm font-bold`,
                    !candidate.selectable || switchBlocked
                      ? `cursor-not-allowed bg-surface text-muted`
                      : `bg-primary text-primary-foreground hover:bg-primary/90`,
                  ].join(` `)}
                  onClick={() => handleSwitch(candidate)}
                >
                  {switchSelection.isLoading
                    ? `Switching...`
                    : `Use ${sourceDataLabel(candidate.sourceSystem)}`}
                </button>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-sm font-semibold text-muted">
              <span className="rounded-full border border-border bg-background px-3 py-1">
                {QUALITY_LABELS[candidate.quality]}
              </span>
              {formatCoveragePercent(candidate.coveragePercent) ? (
                <span className="rounded-full border border-border bg-background px-3 py-1">
                  {formatCoveragePercent(candidate.coveragePercent)}
                </span>
              ) : null}
              {candidate.measurementBasis === `GRID_IMPORT` ? (
                <span className="rounded-full border border-warning/40 bg-warning/15 px-3 py-1 text-foreground">
                  Grid import
                </span>
              ) : null}
            </div>
            {selectionOnly &&
            !candidate.selected &&
            candidate.sourceSystem !== `ARCADIA_PLUG` ? (
              <p className="mt-3 text-sm font-semibold text-muted">
                {SELECTION_ONLY_MANUAL_SOURCE_COPY}
              </p>
            ) : null}
            {!candidate.selected && candidate.selectionBlockedReason ? (
              <p className="mt-3 text-sm font-semibold text-muted">
                {BLOCKED_REASON_COPY[candidate.selectionBlockedReason]}
              </p>
            ) : null}
            {!candidate.selected &&
            pendingConfirmationProfileId === candidate.profileId ? (
              <div
                aria-labelledby={`degraded-consumption-warning-${candidate.profileId}`}
                className="mt-4 rounded-xl border border-warning/40 bg-warning/15 p-4"
                role="alert"
              >
                <p
                  className="font-display text-base font-bold text-foreground"
                  id={`degraded-consumption-warning-${candidate.profileId}`}
                >
                  Confirm degraded usage
                </p>
                <p className="mt-2 text-sm font-semibold leading-6 text-foreground">
                  These readings may represent grid import rather than complete
                  household consumption. Confirm that you want to use them for
                  this proposal.
                </p>
                {selectionOnly ? (
                  <p className="mt-2 text-sm font-semibold leading-6 text-foreground">
                    {SELECTION_ONLY_GRID_IMPORT_COPY}
                  </p>
                ) : null}
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={switchBlocked}
                    onClick={() => handleSwitch(candidate, true)}
                  >
                    {switchSelection.isLoading
                      ? `Switching...`
                      : `Confirm and use Green Button data`}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-bold text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={switchSelection.isLoading}
                    onClick={() => setPendingConfirmationProfileId(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {switchSelection.isError ? (
        <p aria-live="polite" className="mt-3 text-sm font-semibold text-danger">
          {selectionOnly
            ? parseAxiosError(
                switchSelection.error,
                `Unable to switch the consumption source. Please try again.`,
              )
            : switchSelection.error instanceof Error
              ? switchSelection.error.message
              : `Unable to switch the consumption source. Please try again.`}
        </p>
      ) : null}
    </section>
  )
}
